import * as React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  componentName?: string;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { componentName } = this.props;

    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        tags: {
          component: componentName || 'unknown',
          source: 'react_error_boundary',
        },
        extra: {
          componentStack: errorInfo.componentStack,
        },
      });
    }

    console.error(
      `[ErrorBoundary] ${componentName || 'Component'} crashed:`,
      error,
      errorInfo.componentStack
    );
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="alert alert-danger" role="alert">
          <strong>Something went wrong.</strong>
          <p className="mb-2">
            This section failed to load. Please try reloading the page.
          </p>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
